import React from 'react'
import classNames from 'classnames'
import moment from 'moment'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Select } from '@blueprintjs/select'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown'
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight'
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp'
import { faDiagramNested } from '@fortawesome/pro-regular-svg-icons/faDiagramNested'
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan'
import { faUserCheck } from '@fortawesome/pro-regular-svg-icons/faUserCheck'
import { faEllipsisVertical } from '@fortawesome/pro-light-svg-icons/faEllipsisVertical'
import { faStopwatch } from '@fortawesome/pro-regular-svg-icons/faStopwatch'
import {
    Button,
    Checkbox,
    Divider,
    Menu,
    MenuDivider,
    MenuItem,
    Tag,
    Tooltip,
    Popover,
    Icon,
    Spinner,
} from '@blueprintjs/core'

import ConfirmStatusAlert from '../../Controls/Dialogs/ConfirmStatusAlert'
import IconMenu from 'src/components/IconMenu'
import StatusIcon from 'src/components/StatusIcon'
import CustomTag from 'src/components/CustomTag'
import {
    getRunbook,
    asyncUpdateRunbookSession,
    DR_SOLUTIONS_RESTART,
    DR_SOLUTIONS_FAILOVER,
} from 'src/store/modules/runbooks'
import {
    getAction,
    updateAction,
    statusOptions,
    focusAction,
    markActionInfoOnly,
    toggleFreezeOwner,
    expandAction,
    PENDING,
} from 'src/store/modules/runbooks/actions'
import {
    toggleControl,
    singleEditAction,
    openContactSearch,
    setActionId,
} from 'src/store/modules/ui/runbook/controls'
import { formatDate } from 'src/utils/date'

import styles from './action.module.scss'

const DEPENDENCY_COLOR = {
    1: '#f29d49',
    4: '#15b371',
    5: '#e74a5a',
    0: '#4c9fce',
}

const DEPTH_LEFT_PADDING = 0

class Action extends React.PureComponent {
    state = {
        actionStatus: this.props.actionStatus,
        showConfirmationModal: false,
        currentActionId: null,
    }

    componentDidUpdate(prevProps) {
        const { actionStatus } = this.props

        if (prevProps.actionStatus !== actionStatus) {
            this.setState({ actionStatus })
        }
    }

    toggleConfirmationModalOpen = () => {
        this.setState((prevState) => ({
            showConfirmationModal: !prevState?.showConfirmationModal,
        }))
    }
    handleExpandAction = () => this.props.actions.expandAction()

    getCurStatus() {
        return statusOptions.byId[this.props.actionStatus]
    }

    handleCheckboxChange = () => this.props.onCheckboxChange(this)

    handleControlsToggle(toggle) {
        this.props.actions.singleEditAction()
        this.props.actions.toggleControl(toggle)
    }

    handleEditParents = () => this.handleControlsToggle('isEditingParents')

    handleMoveAction = () => this.handleControlsToggle('isMovingAction')

    handleInsertChild = () =>
        this.handleControlsToggle('isInsertingActionAsChild')

    handleInsertSibling = () =>
        this.handleControlsToggle('isInsertingActionAsSibling')

    handleInsertDecisionClick = () =>
        this.handleControlsToggle('isInsertingDecision')

    handleInsertIssueClick = () => this.handleControlsToggle('isInsertingIssue')

    handleConfirmStatusChange = (actionId) => {
        this.handleControlsToggle('isConfirmingStatusChangePropagate')
        this.props.actions.setActionId(actionId)
    }

    handleDeleteAction = () => this.handleControlsToggle('isDeletingAction')

    handleShowCis = () => this.handleControlsToggle('isShowingCIs')

    handleInsertActivityClick = () =>
        this.handleControlsToggle('isInsertingActivity')

    selectStatusItemRenderer = (status) => {
        const { actionStatus } = this.state
        const { byId } = statusOptions

        return (
            <MenuItem
                active={status === actionStatus}
                key={status}
                onClick={() => {
                    this.handleStatusChange(status)
                }}
                text={
                    <div className={styles.selectStatusMenuItem}>
                        <StatusIcon
                            className={styles.selectStatusItemIcon}
                            status={status}
                        />

                        {byId[status].value}
                    </div>
                }
            />
        )
    }

    handleRefreshSolution = () => {
        this.props.actions.singleEditAction()
        this.props.actions.toggleControl('isRefreshingCmdb')
    }
    toggleAddSolution = () => {
        this.props.actions.singleEditAction()
        this.props.actions.toggleControl('isManipulatingSolution')
    }

    setActionStatus = (id, propagate = false) => {
        const { actions, runbookState } = this.props
        const action = statusOptions.byId[id].onSelect

        // Collect hidden action UIDs from the runbook
        const hiddenActionUids = []
        const actionsById = runbookState?.actions?.byId || {}

        for (const uid in actionsById) {
            const act = actionsById[uid]
            if (!act) continue

            // Check if filtered by user or action
            const isFiltered = act.filteredByUser || act.filteredByAction

            // Also check if it's an empty DR Solution (these should be auto-completed)
            const isEmptyDRSolution =
                act.deviceClass === 'DR Solution' &&
                (!act.treeChildren || act.treeChildren.length === 0)

            if (isFiltered || isEmptyDRSolution) {
                hiddenActionUids.push(parseInt(uid))
            }
        }

        actions.asyncUpdateAction(
            {
                action,
                propagate,
            },
            hiddenActionUids
        )

        actions.updateAction({ status: id })
    }

    handleChangesAfterModal = (id) => {
        const { action } = this.props
        if (action.children.length) {
            this.handleConfirmStatusChange(id)
        } else {
            this.setActionStatus(id)
        }
    }

    handleStatusChange = (id) => {
        const { actionStatus } = this.props

        if (actionStatus === PENDING) {
            this.setState({
                showConfirmationModal: true,
                currentActionId: id,
            })
        } else {
            this.handleChangesAfterModal(id)
        }
    }

    handleReverseChange = () => {
        this.props.actions.updateAction({ reverse: !this.props.action.reverse })
    }

    handleSucceedChange = () => {
        this.props.actions.updateAction({
            autoSucceed: !this.props.action.autoSucceed,
        })
    }

    handleChangeInfoOnly = () => {
        // this.props.actions.updateAction({ infoOnly: !this.props.action.infoOnly })
        this.props.actions.markActionInfoOnly()
    }

    handleFreezeOwner = () => {
        this.props.actions.toggleFreezeOwner()
    }

    handleUnmarkAction = () => {
        this.props.actions.updateAction({ markedForDeletion: false })
    }

    openUrl = (url) => {
        window.open(url, '_blank')
    }

    renderDocUrls = () => {
        const { action, mode, parameters } = this.props
        if (!action.docLink) {
            return null
        }
        if (mode === DR_SOLUTIONS_RESTART) {
            return parameters.map((param) => (
                <MenuItem
                    key={`${action.uid}${param}`}
                    icon="selection"
                    text={param}
                    href={`${action.docLink}#Restart_${param.replace(
                        '-',
                        '_'
                    )}`}
                    target="_blank"
                />
            ))
        }
        if (mode === DR_SOLUTIONS_FAILOVER) {
            return parameters
                .slice(0, 1)
                .map((param) => (
                    <MenuItem
                        key={`${action.uid}${param}`}
                        icon="selection"
                        text={param}
                        href={`${action.docLink}#Failover_${param.replace(
                            '-',
                            '_'
                        )}`}
                        target="_blank"
                    />
                ))
        }
        return null
    }

    canChangeStatus() {
        const {
            activeSession,
            isOwner,
            isAdmin,
            action,
            user,
            currentUserIsGlobalAdmin,
            awaitingActionUpdate,
        } = this.props

        const isActionOwner = action.ownerUserId === user.id
        const isTeamMember = user.teams.includes(action.ownerTeamId)
        const canEditAction =
            isOwner ||
            isAdmin ||
            isActionOwner ||
            currentUserIsGlobalAdmin ||
            isTeamMember

        const isAwaitingActionUpdate = awaitingActionUpdate.length === 0

        return activeSession && canEditAction && isAwaitingActionUpdate
    }

    isChild() {
        const { action, actionTreeParent } = this.props
        return !!(
            actionTreeParent && actionTreeParent.children.includes(action.uid)
        )
    }

    isExpanded() {
        const { action } = this.props
        return !!(action.expanded && action.children.length > 0)
    }

    canChangeOwner() {
        const isAdminOrOwner =
            this.props.isOwner ||
            this.props.isAdmin ||
            this.props.currentUserIsGlobalAdmin
        return (
            !this.props.action.infoOnly &&
            // is admin/owner of runbook
            ((this.props.isEditing && isAdminOrOwner) ||
                // is owner of the action => can change owner during execution
                (this.props.activeSession &&
                    (isAdminOrOwner ||
                        this.props.user.id === this.props.action.ownerUserId ||
                        this.props.user.teams.includes(
                            this.props.action.ownerTeamId
                        ))))
        )
    }

    handleChangeOwner = ({ userOnly }) => () => {
        if (!this.canChangeOwner()) return
        this.props.actions.singleEditAction()
        this.props.actions.openContactSearch(userOnly)
    }

    handleContactClear = () => () => {
        if (this.props.runbookId && !this.props.isEditing) {
            this.props.actions.asyncUpdateAction({
                action: 'transfer',
                // TODO: Find a way to clear async contact
                contactType: 'clear',
                propagate: false,
            })
        } else {
            this.props.actions.updateAction({
                ownerSupplier: null,
                ownerTeam: null,
                ownerUser: null,
                ownerSupplierId: null,
                ownerTeamId: null,
                ownerUserId: null,
            })
        }
    }

    handleTakeOwnership = () => {
        const localUser = this.props.user
        if (this.props.runbookId && !this.props.isEditing) {
            this.props.actions.asyncUpdateAction({
                action: 'transfer',
                // TODO: Find a way to clear async contact
                contactType: 'user',
                propagate: false,
                id: localUser.id,
                label: localUser.fullName,
            })
        } else {
            this.props.actions.updateAction({
                ownerUser: localUser.fullName,
                ownerUserId: localUser.id,
            })
        }
    }

    handleFocusAction = (uid) => () => this.props.actions.focusAction(uid)

    handleToggleActionDetails = (tabIdToOpen) => {
        const { actions } = this.props

        actions.singleEditAction()
        actions.toggleControl('isDrawerOpen', {
            activeActionDetailsDrawerTab: tabIdToOpen,
        })
    }

    _renderUid = () => {
        const { action, runbookId, isDragging, uids } = this.props

        return (
            <div className={styles.uidWrapper}>
                {/* Add expand/collapse button */}
                {action.children.length > 0 && (
                    <FontAwesomeIcon
                        icon={this.isExpanded() ? faChevronUp : faChevronDown}
                        className={styles.expandIcon}
                        onClick={this.handleExpandAction}
                    />
                )}

                <Tooltip
                    minimal
                    content={
                        this.isChild()
                            ? 'Actions like this can start in parallel with other actions on the same level and dependent of the same parent'
                            : 'Actions like this one wait for the action above them to complete before they can start'
                    }
                    position="bottom"
                >
                    <div
                        style={{
                            color: this.isChild() ? '#4c9fce' : '#5f7787',
                        }}
                    >
                        <span>
                            {uids.indexOf(action.uid) + 1 + '/' + action.uid}
                        </span>
                        {runbookId && !action.id ? (
                            <span className={styles.asterisk}>*</span>
                        ) : null}

                        <div
                            className={classNames(styles.triangle, {
                                [styles.isDragging]: isDragging,
                            })}
                            style={{
                                '--triangle-left': `${
                                    action.depth * DEPTH_LEFT_PADDING
                                }px`,
                                borderTopColor: this.getCurStatus().bgColor,
                            }}
                        />
                    </div>
                </Tooltip>
            </div>
        )
    }

    _renderCheckboxAndDragHandle = (dragHandleProps) => {
        const { action, isEditing, activeSession } = this.props

        return (
            <div className={styles.checkboxAndDragHandle}>
                {isEditing && (
                    <div {...dragHandleProps}>
                        <FontAwesomeIcon icon={faGripVertical} />
                    </div>
                )}

                <Checkbox
                    checked={action.selected}
                    style={{
                        display: isEditing || activeSession ? 'block' : 'none',
                    }}
                    onChange={this.handleCheckboxChange}
                    className={styles.checkbox}
                />
            </div>
        )
    }

    _renderActionName = () => {
        const { action } = this.props
        const nrOfFilters = action.filters?.length || null

        return (
            <Tooltip
                disabled={!action.description}
                content={action.description}
            >
                <div className={styles.actionName}>
                    <span
                        onClick={() =>
                            this.handleToggleActionDetails('details')
                        }
                        className={styles.actionNameIndented}
                        style={{
                            '--action-depth': action.depth,
                        }}
                    >
                        {action.treeParent && (
                            <FontAwesomeIcon
                                icon={faDiagramNested}
                                className={styles.depthIcon}
                            />
                        )}
                        {action.name}
                    </span>
                    {action.children.length ? (
                        <Tag round className={styles.childrenNumber}>
                            {action.children.length}
                        </Tag>
                    ) : null}
                    {nrOfFilters ? (
                        <CustomTag
                            color={'blue'}
                            size="small"
                            isRound
                            className={styles.childrenFilter}
                        >
                            {nrOfFilters === 1
                                ? `1 Filter`
                                : `${nrOfFilters} Filters`}
                        </CustomTag>
                    ) : null}
                </div>
            </Tooltip>
        )
    }

    renderActionStartTime = (action, runbookStartTime, potentialStartTime) => {
        if (action.infoOnly) return <></>
        if (action.startTime)
            return (
                <span className={styles.startTime}>
                    {formatDate(action.startTime)}
                </span>
            )
        if (runbookStartTime && potentialStartTime) {
            return (
                <Tooltip
                    key={`time-${action.uid}-${potentialStartTime}`}
                    content={`This is a forecasted start time.${
                        action.doNotStartBeforeTime
                            ? ' The action should not start before ' +
                              action.doNotStartBeforeTime
                            : ''
                    }`}
                    position="bottom"
                >
                    <span className={styles.potentialStartTime}>
                        {formatDate(potentialStartTime)}
                    </span>
                </Tooltip>
            )
        }
        return <></>
    }

    renderActionEndTime = (action, runbookStartTime, potentialEndTime) => {
        if (action.infoOnly) return <></>
        if (action.endTime || (runbookStartTime && potentialEndTime))
            return (
                <Tooltip
                    key={`time-${action.uid}`}
                    content={
                        action.endTime
                            ? formatDate(new Date(action.endTime))
                            : `Forecasted end time: ${formatDate(
                                  potentialEndTime
                              )}`
                    }
                    position="bottom"
                    className={styles.stopWatchTooltip}
                >
                    <span
                        className={
                            action.endTime
                                ? styles.stopwatchIcon
                                : styles.potentialStopWatchicon
                        }
                    >
                        <FontAwesomeIcon icon={faStopwatch} size="lg" />
                    </span>
                </Tooltip>
            )

        return <></>
    }

    _renderActionTime = () => {
        const { action, runbookStartTime, isEditing } = this.props
        let potentialStartTime = null
        let potentialEndTime = null
        if (!isEditing && runbookStartTime) {
            // the potential time of an action is calculated by adding
            // the potential start/end time minutes to the start time of the runbook
            if (action.potentialStartTime !== null) {
                potentialStartTime = moment(runbookStartTime)
                    .add(action.potentialStartTime, 'minutes')
                    .toDate()
            }
            if (action.potentialEndTime !== null) {
                potentialEndTime = moment(runbookStartTime)
                    .add(action.potentialEndTime, 'minutes')
                    .toDate()
            }
        }

        return (
            <div className={styles.timeWrapper}>
                {this.renderActionStartTime(
                    action,
                    runbookStartTime,
                    potentialStartTime
                )}
                {this.renderActionEndTime(
                    action,
                    runbookStartTime,
                    potentialEndTime
                )}
            </div>
        )
    }

    _renderActionDependencies = () => {
        const { action, actionParents } = this.props
        const showParents = actionParents.length > 0

        return (
            showParents &&
            actionParents.map((actionParent) => (
                <Tooltip
                    key={`${action.uid}-${actionParent.uid}`}
                    content={actionParent.name}
                    position="bottom"
                >
                    <Tag
                        style={{
                            backgroundColor:
                                this.isExpanded() && actionParent.status === 0
                                    ? 'white'
                                    : DEPENDENCY_COLOR[actionParent.status],
                            color:
                                this.isExpanded() && actionParent.status === 0
                                    ? DEPENDENCY_COLOR[actionParent.status]
                                    : 'white',
                        }}
                        className={styles.dependencyTag}
                        onClick={this.handleFocusAction(actionParent.uid)}
                    >
                        {actionParent.uid}
                    </Tag>
                </Tooltip>
            ))
        )
    }

    _renderActionOwner = () => {
        const { action, user } = this.props

        let owner = false
        let limitedSearch = ''
        if (action.ownerUser) {
            limitedSearch = `user_only=${action.ownerUserId}`
            owner = action.ownerUser
        } else if (action.ownerTeam) {
            limitedSearch = `team_only=${action.ownerTeamId}`
            owner = action.ownerTeam
        }
        const canChangeOwner = this.canChangeOwner()

        return (
            <div
                style={{
                    paddingLeft: 0 * action.depth,
                }}
            >
                {owner ? (
                    <Popover
                        minimal
                        position="bottom-right"
                        onClose={() =>
                            this.setState({
                                isOpenSessionActions: false,
                            })
                        }
                        disabled={!canChangeOwner}
                        content={
                            <Menu>
                                <MenuItem
                                    icon={
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    }
                                    className={classNames(
                                        styles.ownerMenuIcon,
                                        styles.ownerIconMargin
                                    )}
                                    text="Assign"
                                    onClick={this.handleChangeOwner({
                                        userOnly:
                                            this.props.isAdmin ||
                                            this.props
                                                .currentUserIsGlobalAdmin ||
                                            this.props.isOwner
                                                ? false
                                                : limitedSearch,
                                    })}
                                />
                                {action.ownerUserId !== user.id ? (
                                    <MenuItem
                                        icon={
                                            <FontAwesomeIcon
                                                icon={faUserCheck}
                                            />
                                        }
                                        className={styles.ownerMenuIcon}
                                        text="Take"
                                        onClick={this.handleTakeOwnership}
                                    />
                                ) : null}
                                <MenuItem
                                    icon={<FontAwesomeIcon icon={faTrashCan} />}
                                    className={classNames(
                                        styles.ownerMenuIcon,
                                        styles.ownerIconMargin
                                    )}
                                    text="Delete"
                                    onClick={this.handleContactClear()}
                                />
                            </Menu>
                        }
                    >
                        <Tag
                            round
                            className={styles.ownerTag}
                            disabled={!canChangeOwner}
                        >
                            {owner}
                            {canChangeOwner && (
                                <FontAwesomeIcon icon={faChevronDown} />
                            )}
                        </Tag>
                    </Popover>
                ) : (
                    <Button
                        icon="plus"
                        small
                        intent="primary-fade"
                        disabled={!canChangeOwner}
                        className={styles.expandOwnerButton}
                        onClick={this.handleChangeOwner({ userOnly: false })}
                    >
                        Add owner
                    </Button>
                )}
            </div>
        )
    }

    _renderSelectStatus = () => {
        const { actionStatus } = this.state
        const {
            action,
            isEditing,
            activeSession,
            awaitingActionUpdate = [],
            id,
        } = this.props

        const isUpdating = awaitingActionUpdate.find((item) => item === id)

        if (!!isUpdating) {
            return (
                <div className={styles.spinnerCell}>
                    <Spinner intent={'primary'} size={Icon.SIZE_STANDARD} />
                </div>
            )
        }

        return !action.infoOnly && action.id && !isEditing && activeSession ? (
            <Select
                items={statusOptions.ids}
                itemRenderer={this.selectStatusItemRenderer}
                filterable={false}
                popoverProps={{
                    minimal: true,
                    position: 'bottom-right',
                }}
                disabled={!this.canChangeStatus()}
                className={styles.selectStatus}
            >
                <Button
                    className={classNames({
                        [styles.isUpdating]: !!awaitingActionUpdate.length,
                    })}
                    minimal
                    small
                    rightIcon="chevron-down"
                    disabled={!this.canChangeStatus()}
                    icon={<StatusIcon status={actionStatus} />}
                />
            </Select>
        ) : null
    }

    _renderMenuOptions = () => {
        const { action, isEditing, runbookId, activeSession } = this.props

        return (
            <>
                <IconMenu
                    icon={
                        <FontAwesomeIcon
                            icon={faEllipsisVertical}
                            className={styles.rowIconMenu}
                        />
                    }
                    className={styles.rowMenu}
                    content={
                        <Menu>
                            {isEditing && (
                                <MenuItem
                                    icon="changes"
                                    text="Add solution"
                                    onClick={this.toggleAddSolution}
                                />
                            )}
                            {isEditing && (
                                <MenuItem
                                    icon="changes"
                                    text="Add device(s)"
                                    onClick={() =>
                                        this.handleControlsToggle(
                                            'isAddingDevices'
                                        )
                                    }
                                />
                            )}
                            {isEditing &&
                            runbookId !== null &&
                            (action.isAvZone || action.device) ? (
                                <MenuItem
                                    icon="refresh"
                                    text="Refresh from CMDB"
                                    onClick={this.handleRefreshSolution}
                                />
                            ) : null}
                            {isEditing && <MenuDivider />}
                            {isEditing && (
                                <MenuItem
                                    icon="diagram-tree"
                                    text="Edit parent actions"
                                    onClick={this.handleEditParents}
                                />
                            )}
                            {isEditing && (
                                <MenuItem
                                    icon="layers"
                                    text="Add split branch action"
                                    onClick={this.handleInsertChild}
                                />
                            )}
                            {isEditing && (
                                <MenuItem
                                    icon="layer"
                                    text="Add continue branch action"
                                    onClick={this.handleInsertSibling}
                                />
                            )}
                            {isEditing && (
                                <MenuItem
                                    icon="arrows-vertical"
                                    text="Move after action"
                                    onClick={this.handleMoveAction}
                                />
                            )}
                            {isEditing && <MenuDivider />}
                            {isEditing && action.markedForDeletion && (
                                <MenuItem
                                    icon="pin"
                                    text="Unmark for deletion"
                                    onClick={this.handleUnmarkAction}
                                />
                            )}
                            {isEditing &&
                                (action.freezeOwnerTeam ? (
                                    <MenuItem
                                        icon="star"
                                        text="Unfreeze Owner Team"
                                        onClick={this.handleFreezeOwner}
                                    />
                                ) : (
                                    <MenuItem
                                        icon="star-empty"
                                        text="Freeze Owner Team"
                                        onClick={this.handleFreezeOwner}
                                    />
                                ))}
                            {isEditing &&
                                (!action.infoOnly ? (
                                    <MenuItem
                                        icon="info-sign"
                                        text="Mark as Info only"
                                        onClick={this.handleChangeInfoOnly}
                                    />
                                ) : (
                                    <MenuItem
                                        icon="info-sign"
                                        text="Unmark Info only"
                                        onClick={this.handleChangeInfoOnly}
                                    />
                                ))}
                            {isEditing && (
                                <MenuItem
                                    icon="edit"
                                    text="Edit"
                                    onClick={() =>
                                        this.handleControlsToggle(
                                            'isEditingBaseFields'
                                        )
                                    }
                                />
                            )}

                            {isEditing && <MenuDivider />}
                            {isEditing && (
                                <MenuItem
                                    onClick={this.handleDeleteAction}
                                    icon="trash"
                                    text="Delete"
                                />
                            )}
                            {isEditing && <MenuDivider />}
                            {this.renderDocUrls()}
                            <MenuItem
                                disabled={!action.customUrl}
                                icon="globe-network"
                                text="Custom URL"
                                onClick={() => this.openUrl(action.customUrl)}
                            />
                            <MenuItem
                                icon="globe-network"
                                text="Show underlying CIs"
                                onClick={this.handleShowCis}
                            />
                            {activeSession && <Divider />}
                            {activeSession && (
                                <MenuItem
                                    icon="chat"
                                    onClick={() =>
                                        this.handleToggleActionDetails(
                                            'activity'
                                        )
                                    }
                                    text="Add comment"
                                />
                            )}
                            {activeSession && (
                                <MenuItem
                                    icon="comment"
                                    onClick={this.handleInsertDecisionClick}
                                    text="Add decision"
                                />
                            )}
                            {activeSession && (
                                <MenuItem
                                    icon="issue-new"
                                    onClick={this.handleInsertIssueClick}
                                    text="Add issue"
                                />
                            )}
                            {activeSession && (
                                <MenuItem
                                    icon="timeline-events"
                                    onClick={this.handleInsertActivityClick}
                                    text="Add activity"
                                />
                            )}
                            {activeSession && (
                                <MenuItem
                                    disabled
                                    icon="warning-sign"
                                    onClick={this.handleInsertRiskClick}
                                    text="Add risk"
                                />
                            )}
                        </Menu>
                    }
                />
            </>
        )
    }

    render() {
        const {
            action,
            style,
            provided,
            isDragging,
            filterActive,
            headers,
            isCurrentMatch = false,
            isHighlighted = false,
        } = this.props

        const cellsContent = headers.map((header, index) => {
            if (!header || !header.key) {
                return () => null
            }
            switch (header.key) {
                case 'id':
                    return this._renderUid
                case 'select':
                    return () =>
                        this._renderCheckboxAndDragHandle(
                            provided.dragHandleProps
                        )
                case 'status':
                    return this._renderSelectStatus
                case 'actionName':
                    return this._renderActionName
                case 'time':
                    return this._renderActionTime
                case 'dependencies':
                    return this._renderActionDependencies
                case 'owner':
                    return this._renderActionOwner
                case 'menu':
                    return this._renderMenuOptions
                default:
                    return () => null
            }
        })

        let rowBackgroundColor = '#fafbfc'
        let styleAction = styles.action
        if (action.color) {
            rowBackgroundColor = action.color
        }
        if (action.depth > 0) {
            rowBackgroundColor = 'rgba(13, 67, 90, 0.08)'
        }
        if (this.isExpanded() && action.children?.length) {
            rowBackgroundColor = 'rgba(13, 67, 90, 0.08)'
            styleAction = styles.action
        }
        if (action.filteredAction && filterActive) {
            rowBackgroundColor = '#4C9FCE'
            styleAction = styles.expandedAction
        }

        if (isHighlighted) {
            rowBackgroundColor = '#FFEED3'
            styleAction = styles.action
        }

        if (isCurrentMatch) {
            rowBackgroundColor = '#FFDFA9'
            styleAction = styles.action
        }

        return (
            <div
                {...provided.draggableProps}
                ref={provided.innerRef}
                style={{
                    ...provided.draggableProps.style,
                    ...style,
                    right: 0,
                    width: 'auto',
                    transition: 'all 0.2s ease',
                    paddingLeft: DEPTH_LEFT_PADDING * action.depth,
                    opacity: 1,
                }}
                aria-selected={isCurrentMatch}
            >
                {/* To change the width of the cell/headers check the [headers] prop */}
                <div
                    id={`${action.uid}-card`}
                    className={styleAction}
                    style={{
                        // Alternate row background colors if actions does not have a color set
                        background: rowBackgroundColor,
                        border: isDragging
                            ? '2px solid #4c9fce'
                            : isCurrentMatch
                            ? '2px solid #E08B00'
                            : 'none',
                        boxShadow: isDragging
                            ? '0 9px 15px 0 rgba(13,67,90,0.4)'
                            : 'none',
                    }}
                >
                    {headers.map((header, index) => {
                        if (!header) {
                            return null // Skip null or undefined headers
                        }
                        return (
                            <div
                                key={index}
                                style={{
                                    '--header-width': header.width || 'auto',
                                }}
                                className={classNames(styles.actionCell, {
                                    [styles.centerCell]: header.centered,
                                })}
                            >
                                {cellsContent[index]()}
                            </div>
                        )
                    })}
                </div>
                <ConfirmStatusAlert
                    actionId={this.state.currentActionId}
                    showConfirmationModal={this.state.showConfirmationModal}
                    setConfirmationModal={this.toggleConfirmationModalOpen}
                    onConfirm={this.handleChangesAfterModal}
                />
            </div>
        )
    }
}

const mapStateToProps = (state, ownProps) => {
    // TODO: Simplify props for this component
    const act = getAction(state, ownProps.runbookId, ownProps.id)
    const user = state.app.user ?? {
        id: null,
        teams: [],
    }
    const runbookState = state.runbooks.byId[ownProps.runbookId]
    const filterActive = Object.keys(runbookState.filterQuery).length
    const uids = runbookState['actions']['uids']

    const {
        id,
        organization,
        isEditing,
        isAdmin,
        isOwner,
        activeSession,
        parameters,
        mode,
        runbookStartTime,
        actions: { resultIndexes = [] },
    } = getRunbook(state, ownProps.runbookId)
    return {
        runbookId: id,
        parameters,
        mode,
        isEditing,
        organization,
        user,
        uids,
        isAdmin,
        isOwner,
        activeSession,
        filterActive,
        actionStatus: act.status,
        action: act,
        actionParents: act.parents.map((a) =>
            getAction(state, ownProps.runbookId, a)
        ),
        actionChildren: act.children,
        actionTreeParent: getAction(state, ownProps.runbookId, act.treeParent),
        expanded: act.expanded,
        runbookStartTime: runbookStartTime,
        currentUserIsGlobalAdmin: state.app.isAdmin,
        runbookState: runbookState, // Add the full runbook state for accessing actions
        awaitingActionUpdate:
            state.runbooks.byId?.[state.ui.runbook.activeRunbook]
                ?.awaitingActionUpdate,
        resultIndexes,
    }
}

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        dispatch,
        actions: bindActionCreators(
            {
                updateAction: (data) =>
                    updateAction(ownProps.runbookId, ownProps.id, data),
                asyncUpdateAction: (data, hiddenActionUids) =>
                    asyncUpdateRunbookSession(
                        ownProps.runbookId,
                        [ownProps.id],
                        data,
                        hiddenActionUids
                    ),
                singleEditAction: () => singleEditAction(ownProps.id),
                toggleControl,
                openContactSearch,
                setActionId,
                focusAction: (uid) => focusAction(ownProps.runbookId, uid),
                markActionInfoOnly: () =>
                    markActionInfoOnly(ownProps.runbookId, ownProps.id),
                toggleFreezeOwner: () =>
                    toggleFreezeOwner(ownProps.runbookId, ownProps.id),
                expandAction: () =>
                    expandAction(ownProps.runbookId, ownProps.id),
            },
            dispatch
        ),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Action)
